import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiPrinterLine,
  RiDownloadLine,
  RiTimeLine,
  RiCloseCircleLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiInformationLine,
  RiAlertLine,
  RiShieldCheckLine,
  RiArrowLeftLine,
  RiTicketLine,
} from "react-icons/ri";
import styles from "./BusBookingConfirmation2.module.css";

const BusBookingConfirmation2 = ({ mode, onNavigate, templateId, params, styleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#1a3c2e",
    "--secondary-color": styleConfig?.secondary || "#c9a84c",
  };

  const {
    bus, searchQuery, selectedSeats, passengerCount, passengerDetails,
    selectedBoardingPoint, selectedDroppingPoint, totalAmount, baseFare,
    convenienceFee, bookingStatus = "CONFIRMED", bookingId, paymentId, orderId,
    message, bookingNo, pnr, supplierBookingId, invoiceNo,
  } = params || location.state || {};

  const handleNewBooking = () => {
    if (mode === "builder") {
      onNavigate({ name: "bus-search" });
    } else if (mode === "preview") {
      navigate(`/template-preview/${templateId}/bus-search`);
    } else {
      navigate("/template/bus-search");
    }
  };

  if (!bus || !selectedSeats || !passengerDetails) {
    return (
      <div className={styles.wrapper} style={themeVariables}>
        <div className={styles.errorFullPage}>
          <div className={styles.errorCard}>
            <RiAlertLine className={styles.errorIconMain} />
            <h2>Ticket Not Found</h2>
            <p>We couldn't find the details for this booking.</p>
            <button onClick={handleNewBooking} className={styles.errorActionBtn}>
              Go Back to Search
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => `₹${Math.round(parseFloat(amount) || 0).toLocaleString("en-IN")}`;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    } catch (error) { return dateString; }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
  };

  const getStatusConfig = (status) => {
    const configs = {
      CONFIRMED: { icon: RiCheckboxCircleLine, title: "Booking Confirmed!", subtitle: "Your bus tickets have been successfully booked", theme: "success" },
      PAYMENT_SUCCESS: { icon: RiCheckboxCircleLine, title: "Payment Successful!", subtitle: "Your payment was successful. Booking is confirmed.", theme: "success" },
      PAYMENT_FAILED: { icon: RiCloseCircleLine, title: "Payment Failed", subtitle: "Payment was not completed. Please try again.", theme: "danger" },
      PAYMENT_CANCELLED: { icon: RiCloseCircleLine, title: "Payment Cancelled", subtitle: "Payment was cancelled. Please try again.", theme: "warning" },
      PAYMENT_SUCCESS_BOOKING_FAILED: { icon: RiErrorWarningLine, title: "Payment Successful - Booking Issue", subtitle: "Payment successful but booking failed. Our team will contact you.", theme: "warning" },
      PAYMENT_SUCCESS_BOOKING_PENDING: { icon: RiAlertLine, title: "Payment Successful - Processing", subtitle: "Payment successful. Your booking is being processed.", theme: "info" },
    };
    return configs[status] || configs.CONFIRMED;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`<html><body><h1>Ticket Ready for Print: ${bookingNo || pnr}</h1></body></html>`);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const handleDownload = () => {
    const blob = new Blob([`<html><body><h1>Ticket: ${bookingNo || pnr}</h1></body></html>`], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Bus-Ticket-${bookingNo || pnr || 'Booking'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleViewBookings = () => {
    if (mode === "builder") alert("My Bookings is disabled in builder mode.");
    else navigate("/bus/bookings");
  };

  const handleContactSupport = () => {
    const supportPhone = "+91-XXXXXXXXXX";
    const supportEmail = "support@example.com";
    const action = window.confirm(`Contact Support:\n\n📞 Call: ${supportPhone}\n📧 Email: ${supportEmail}`);
    if (action) window.location.href = `tel:${supportPhone}`;
    else window.location.href = `mailto:${supportEmail}`;
  };

  const statusConfig = getStatusConfig(bookingStatus);
  const StatusIcon = statusConfig.icon;
  const displayMessage = message || statusConfig.subtitle;

  return (
    <div className={styles.wrapper} style={themeVariables}>
      <div className={styles.mainContent}>
        <div className={styles.container}>
          {/* Action Bar */}
          <div className={styles.topActions}>
            <button className={styles.backBtn} onClick={handleNewBooking}>
              <RiArrowLeftLine /> Back to Search
            </button>

            <div className={styles.actionBtnGroup}>
              <button className={`${styles.actionBtn} ${styles.printBtn}`} onClick={handlePrint}><RiPrinterLine /> Print</button>
              <button className={`${styles.actionBtn} ${styles.downloadBtn}`} onClick={handleDownload}><RiDownloadLine /> Download</button>
              <button className={`${styles.actionBtn} ${styles.viewBtn}`} onClick={handleViewBookings}><RiFileList3Line /> My Bookings</button>
              <button className={`${styles.actionBtn} ${styles.newBtn}`} onClick={handleNewBooking}><RiBusLine /> New Booking</button>
            </div>
          </div>

          <div className={styles.bookingWrapper}>
            {/* Status Banner */}
            <div className={`${styles.statusBanner} ${styles[statusConfig.theme]}`}>
              <div className={styles.goldLine}></div>
              <div className={styles.statusIconWrapper}><StatusIcon className={styles.statusIcon} /></div>
              <div className={styles.statusContent}>
                <h2 className={styles.statusTitle}>{statusConfig.title}</h2>
                <p className={styles.statusMessage}>{displayMessage}</p>
                <div className={styles.referenceList}>
                  {bookingNo && <div className={styles.referenceItem}><span className={styles.referenceLabel}>Booking No</span><span className={styles.referenceValue}>{bookingNo}</span></div>}
                  {pnr && <div className={styles.referenceItem}><span className={styles.referenceLabel}>PNR Number</span><span className={styles.referenceValue}>{pnr}</span></div>}
                  {invoiceNo && <div className={styles.referenceItem}><span className={styles.referenceLabel}>Invoice</span><span className={styles.referenceValue}>{invoiceNo}</span></div>}
                </div>
              </div>
            </div>

            <div className={styles.twoColumn}>
              <div className={styles.leftColumn}>
                {/* Journey Details */}
                <div className={styles.card}>
                  <div className={styles.goldLine}></div>
                  <div className={styles.cardHeader}><RiBusLine className={styles.cardIcon} /><h3>Journey Details</h3></div>
                  <div className={styles.cardBody}>
                    <div className={styles.routeInfo}>
                      <div className={styles.routePoint}>
                        <div className={styles.pointDot}></div>
                        <div className={styles.pointDetails}>
                          <span className={styles.pointTime}>{formatTime(bus.departureTime)}</span>
                          <span className={styles.pointLocation}>{searchQuery.fromStation}</span>
                          <span className={styles.pointAddress}>{selectedBoardingPoint?.location}</span>
                        </div>
                      </div>
                      <div className={styles.routeConnector}>
                        <span className={styles.durationBadge}>{bus.duration || "Direct"}</span>
                      </div>
                      <div className={styles.routePoint}>
                        <div className={`${styles.pointDot} ${styles.destinationDot}`}></div>
                        <div className={styles.pointDetails}>
                          <span className={styles.pointTime}>{formatTime(bus.arrivalTime)}</span>
                          <span className={styles.pointLocation}>{searchQuery.toStation}</span>
                          <span className={styles.pointAddress}>{selectedDroppingPoint?.location}</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.detailsGrid}>
                      <div className={styles.detailItem}><RiCalendarLine /><div><span className={styles.detailLabel}>Travel Date</span><span className={styles.detailValue}>{formatDate(searchQuery.travelDate)}</span></div></div>
                      <div className={styles.detailItem}><RiUserLine /><div><span className={styles.detailLabel}>Total Pax</span><span className={styles.detailValue}>{passengerCount} Person(s)</span></div></div>
                      <div className={styles.detailItem}><RiBusLine /><div><span className={styles.detailLabel}>Operator</span><span className={styles.detailValue}>{bus.operatorName}</span></div></div>
                      <div className={styles.detailItem}><RiTicketLine /><div><span className={styles.detailLabel}>Type</span><span className={styles.detailValue}>{bus.busType}</span></div></div>
                    </div>
                  </div>
                </div>

                {/* Boarding/Dropping Points */}
                <div className={styles.card}>
                  <div className={styles.goldLine}></div>
                  <div className={styles.cardHeader}><RiMapPinLine className={styles.cardIcon} /><h3>Points Information</h3></div>
                  <div className={styles.cardBody}>
                    <div className={styles.pointsGrid}>
                      <div className={styles.pointSummaryCard}>
                        <h4 className={styles.pointTitle}>Boarding Point</h4>
                        <p><strong>{selectedBoardingPoint?.location}</strong></p>
                        <p className={styles.pointTimeInfo}><RiTimeLine /> {formatTime(selectedBoardingPoint?.time)}</p>
                      </div>
                      <div className={styles.pointSummaryCard}>
                        <h4 className={`${styles.pointTitle} ${styles.droppingTitle}`}>Dropping Point</h4>
                        <p><strong>{selectedDroppingPoint?.location}</strong></p>
                        <p className={styles.pointTimeInfo}><RiTimeLine /> {formatTime(selectedDroppingPoint?.time)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Passenger Details */}
                <div className={styles.card}>
                  <div className={styles.goldLine}></div>
                  <div className={styles.cardHeader}><RiUserLine className={styles.cardIcon} /><h3>Passenger List</h3></div>
                  <div className={styles.cardBody}>
                    <table className={styles.passengerTable}>
                      <thead><tr><th>Seat</th><th>Name & Contact</th><th>Age</th><th>Fare</th></tr></thead>
                      <tbody>
                        {selectedSeats.map((seat, index) => {
                          const p = passengerDetails[index] || passengerDetails[0];
                          return (
                            <tr key={seat.id}>
                              <td><span className={styles.seatBadge}>{seat.id}</span></td>
                              <td><strong>{p?.title} {p?.firstName} {p?.lastName}</strong><br /><small className={styles.mutedText}>{p?.email}</small></td>
                              <td>{p?.age} Yrs</td>
                              <td className={styles.priceCell}>{formatCurrency(seat.totalPriceWithTaxes)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className={styles.rightColumn}>
                {/* Fare Summary */}
                <div className={styles.card}>
                  <div className={styles.goldLine}></div>
                  <div className={styles.cardHeader}><RiMoneyDollarCircleLine className={styles.cardIcon} /><h3>Fare Breakdown</h3></div>
                  <div className={styles.cardBody}>
                    <div className={styles.fareList}>
                      <div className={styles.fareItem}><span>Base Fare</span><span>{formatCurrency(baseFare)}</span></div>
                      <div className={styles.fareItem}><span>Taxes & Fees</span><span>{formatCurrency(convenienceFee)}</span></div>
                      <div className={styles.fareDivider}></div>
                      <div className={`${styles.fareItem} ${styles.totalRow}`}><span>Total Amount</span><span className={styles.totalPrice}>{formatCurrency(totalAmount)}</span></div>
                    </div>
                  </div>
                </div>

                {/* Support Card */}
                <div className={styles.supportCard}>
                  <RiInformationLine className={styles.supportIcon} />
                  <h4>Need Assistance?</h4>
                  <p>Our premium support desk is available 24/7 for all your travel inquiries.</p>
                  <button className={styles.supportBtn} onClick={handleContactSupport}>Contact Concierge</button>
                </div>

                <div className={styles.securityBanner}>
                  <RiShieldCheckLine /><span>Transaction Secure & Verified</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusBookingConfirmation2;